'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedCallback } from '@kit/ui/hooks/use-debounce';

import { useGoogleMapsAPI } from '../../../lib/use-google-maps';
import type { AddressValue } from './types';

// =====================================================================
// Google Maps type declarations (minimal, internal)
//
// Targets the Places "new" surface — `AutocompleteSuggestion` +
// `Place` — which replaced `AutocompleteService` + `PlacesService` on
// 2025-03-01 (Google emits a console warning on every legacy
// construction; Sentry BALLEE-HD). Mirrors the shapes already used by
// `apps/web/components/address-lookup.tsx`.
// =====================================================================

interface GooglePlacePrediction {
  placeId: string;
  text: { text: string };
  mainText: { text: string };
  secondaryText?: { text: string };
}

interface GoogleAutocompleteSuggestion {
  placePrediction?: GooglePlacePrediction;
}

interface AutocompleteRequest {
  input: string;
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
}

interface PlaceAddressComponent {
  types: string[];
  longText?: string;
  shortText?: string;
}

interface GooglePlace {
  id?: string;
  addressComponents?: PlaceAddressComponent[];
  fetchFields(opts: { fields: string[] }): Promise<unknown>;
}

interface GoogleMapsPlaces {
  AutocompleteSuggestion?: {
    fetchAutocompleteSuggestions(
      request: AutocompleteRequest,
    ): Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>;
  };
  Place?: new (opts: { id: string }) => GooglePlace;
}

function getPlacesLibrary(): GoogleMapsPlaces | undefined {
  // Called during render too; `'use client'` still server-renders.
  if (typeof window === 'undefined') return undefined;
  return (
    window as unknown as {
      google?: { maps?: { places?: GoogleMapsPlaces } };
    }
  ).google?.maps?.places;
}

export interface AutocompletePrediction {
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

// =====================================================================
// Constants
// =====================================================================

/**
 * Common-market country allowlist for the Places `includedRegionCodes`.
 * Hardcoding a single default ('ch') stranded every non-Swiss dancer
 * (Sentry thread + Jieun Aurora Vienna report 2026-05-21): the
 * predictions API silently returns ZERO_RESULTS for foreign addresses
 * and the user has no cue. List covers Ballee's active dancer
 * geographies.
 */
export const ADDRESS_AUTOCOMPLETE_COUNTRIES = [
  'ch',
  'at',
  'de',
  'fr',
  'it',
  'gb',
  'us',
  'es',
  'nl',
  'be',
  'pt',
  'pl',
  'ie',
  'lu',
  'li',
] as const;

/**
 * Street-level primary types for the Autocomplete (New) request. The
 * legacy `types: ['address']` collection is NOT accepted by the new API
 * (verified 2026-08-22 against places.googleapis.com: "Invalid
 * included_primary_types 'address'"); these four Table-B types are the
 * documented street-address equivalent.
 */
export const ADDRESS_AUTOCOMPLETE_PRIMARY_TYPES = [
  'street_address',
  'premise',
  'subpremise',
  'route',
] as const;

/** Hard cap on how long we wait for a Google Places promise to settle. */
export const PLACES_CALLBACK_TIMEOUT_MS = 5_000;

/**
 * Trigger threshold for the "Autocomplete unavailable" hint. We tolerate
 * one transient timeout (e.g. user typed faster than the network) before
 * we declare autocomplete dead and offer the manual-entry hint.
 */
export const PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS = 2;

// =====================================================================
// Helpers
// =====================================================================

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

// =====================================================================
// Hook
// =====================================================================

export type ResolvedPlace = Omit<AddressValue, 'apartment'> & {
  placeId: string;
};

export interface UsePlacesParams {
  apiKey: string | undefined;
  /** ISO-3166-1 alpha-2 country picked in the form, if any (used to narrow predictions). */
  pickedCountryCode: string | undefined;
  /**
   * Notified when the Places integration fails. The wrapper component
   * forwards this to the consumer's `onAutocompleteError` prop and to a
   * `console.warn` breadcrumb for Sentry.
   */
  onError: (error: Error, op: 'getDetails' | 'getPredictions' | 'load') => void;
}

export interface UsePlacesResult {
  /** Predictions to render in the dropdown. */
  placePredictions: AutocompletePrediction[];
  /** True while predictions are in-flight. Drives the input-trailing spinner. */
  isPredictionsLoading: boolean;
  /** True while a `getDetails` call is resolving. Also drives the spinner. */
  isLoadingPlace: boolean;
  /**
   * True when the autocomplete is declared unavailable (no API key, load
   * error, or 2+ consecutive callback timeouts). The component renders
   * the manual-entry hint and stops re-arming the spinner.
   */
  autocompleteFailed: boolean;
  /**
   * Debounced predictions trigger. Pass the current Street Address text
   * — emits at most one Places request every 350ms.
   */
  getPlacePredictions: (input: string) => void;
  /**
   * Resolves a selected `place_id` into the structured address slot.
   * Returns `null` on failure (already reported via `onError`).
   */
  resolvePlaceDetails: (placeId: string) => Promise<ResolvedPlace | null>;
  /** Clear predictions (e.g. after a selection or on close). */
  clearPredictions: () => void;
}

/**
 * Encapsulates the Google Places lifecycle: API load, readiness of the
 * `AutocompleteSuggestion` + `Place` classes, debounced predictions with
 * timeout + fail-open hint state, and `Place.fetchFields` parsing. Keep the component
 * itself focused on rendering + RHF wiring.
 */
export function usePlaces({
  apiKey,
  pickedCountryCode,
  onError,
}: UsePlacesParams): UsePlacesResult {
  // Stable ref so debounced closures don't capture a stale handler.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const reportFailure = useCallback(
    (error: Error, op: 'getDetails' | 'getPredictions' | 'load') => {
      // Greppable client breadcrumb. Sentry instrumentation captures
      // console.warn as a breadcrumb on the wrapping page.
      console.warn(`[address-autocomplete] ${op} failed: ${error.message}`);
      onErrorRef.current?.(error, op);
    },
    [],
  );

  // ---------------------------------------------------------------------
  // API loader
  // ---------------------------------------------------------------------
  const { error: googleError, isLoaded: isGoogleLoaded } = useGoogleMapsAPI({
    apiKey: apiKey ?? '',
    libraries: ['places'],
  });

  const [autocompleteFailed, setAutocompleteFailed] = useState(false);
  const consecutiveTimeoutsRef = useRef(0);

  // Fail-open when Google Maps itself failed to load OR when the API key
  // is missing. Manual entry stays available regardless.
  useEffect(() => {
    if (!apiKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- script-loader effect (external-system sync); the failure flag is also set from async load/error callbacks, so it can't be derived purely during render.
      setAutocompleteFailed(true);
      return;
    }
    if (googleError) {
      setAutocompleteFailed(true);
      reportFailure(googleError, 'load');
      return;
    }
    // Loaded, but the new Places surface is missing (pinned old
    // `version`, partial load): declare autocomplete dead instead of
    // leaving a field that never responds.
    if (isGoogleLoaded && !getPlacesLibrary()?.AutocompleteSuggestion) {
      setAutocompleteFailed(true);
      reportFailure(
        new Error('Places AutocompleteSuggestion not available'),
        'load',
      );
    }
  }, [apiKey, googleError, isGoogleLoaded, reportFailure]);

  // ---------------------------------------------------------------------
  // Services
  // ---------------------------------------------------------------------
  // The new Places surface is static (`AutocompleteSuggestion.fetch…`)
  // and class-per-call (`new Place({ id })`), so there is no service
  // instance to hold. We only track whether the library is usable.
  const isPlacesReady =
    isGoogleLoaded && !!apiKey && !!getPlacesLibrary()?.AutocompleteSuggestion;

  // Resolve component-restrictions country list. If the form has a
  // 2-letter country picked, restrict to that for relevance; otherwise
  // fall back to the active-geography allowlist so non-Swiss dancers
  // aren't stranded on a zero-results loop.
  // `includedRegionCodes` accepts at most 15 codes; the allowlist is
  // exactly 15.
  const regionCodes = useMemo<string[]>(() => {
    const picked = pickedCountryCode?.toLowerCase();
    if (picked && picked.length === 2) return [picked];
    return [...ADDRESS_AUTOCOMPLETE_COUNTRIES];
  }, [pickedCountryCode]);

  // ---------------------------------------------------------------------
  // Predictions
  // ---------------------------------------------------------------------
  const [placePredictions, setPlacePredictions] = useState<
    AutocompletePrediction[]
  >([]);
  const [isPredictionsLoading, setIsPredictionsLoading] = useState(false);

  const requestPlacePredictions = useCallback(
    (input: string) => {
      const places = getPlacesLibrary();
      if (!isPlacesReady || !places?.AutocompleteSuggestion || !input.trim()) {
        setPlacePredictions([]);
        setIsPredictionsLoading(false);
        return;
      }

      setIsPredictionsLoading(true);

      const request: AutocompleteRequest = {
        input,
        includedPrimaryTypes: [...ADDRESS_AUTOCOMPLETE_PRIMARY_TYPES],
        includedRegionCodes: regionCodes,
      };

      // Materialise the call inside a promise so a synchronous throw from
      // the Google entry point takes the same catch/finally path as an
      // async rejection (otherwise the spinner sticks).
      const suggestions = places.AutocompleteSuggestion;
      withTimeout(
        new Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>(
          (resolve) =>
            resolve(suggestions.fetchAutocompleteSuggestions(request)),
        ),
        PLACES_CALLBACK_TIMEOUT_MS,
        'getPlacePredictions',
      )
        .then((response): AutocompletePrediction[] =>
          response.suggestions
            .map((s) => s.placePrediction)
            .filter((p): p is GooglePlacePrediction => p !== undefined)
            .map((p) => ({
              place_id: p.placeId,
              structured_formatting: {
                main_text: p.mainText.text,
                secondary_text: p.secondaryText?.text ?? '',
              },
            })),
        )
        .then((predictions) => {
          consecutiveTimeoutsRef.current = 0;
          setPlacePredictions(predictions);
        })
        .catch((error: Error) => {
          setPlacePredictions([]);
          const isTimeout = error.message.includes('timed out');
          if (isTimeout) {
            consecutiveTimeoutsRef.current += 1;
            if (
              consecutiveTimeoutsRef.current >=
              PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS
            ) {
              setAutocompleteFailed(true);
            }
          }
          reportFailure(error, 'getPredictions');
        })
        .finally(() => {
          setIsPredictionsLoading(false);
        });
    },
    [isPlacesReady, regionCodes, reportFailure],
  );

  // Debounce predictions by 350ms — the Places service is rate-limited
  // and un-debounced rapid typing produces overlapping in-flight
  // requests that can silently drop their callbacks (the original
  // Jieun-stuck-spinner failure mode).
  const getPlacePredictions = useDebouncedCallback(
    requestPlacePredictions as (...args: unknown[]) => void,
    350,
  ) as (input: string) => void;

  const clearPredictions = useCallback(() => {
    setPlacePredictions([]);
  }, []);

  // ---------------------------------------------------------------------
  // Details
  // ---------------------------------------------------------------------
  const [isLoadingPlace, setIsLoadingPlace] = useState(false);

  const resolvePlaceDetails = useCallback(
    async (placeId: string): Promise<ResolvedPlace | null> => {
      if (!apiKey) return null;
      const places = getPlacesLibrary();
      if (!places?.Place) {
        reportFailure(new Error('Places library not available'), 'getDetails');
        return null;
      }

      setIsLoadingPlace(true);
      try {
        const place = new places.Place({ id: placeId });
        await withTimeout(
          place.fetchFields({
            fields: ['addressComponents', 'id'],
          }),
          PLACES_CALLBACK_TIMEOUT_MS,
          'getDetails',
        );

        const components = place.addressComponents ?? [];

        let streetNumber = '';
        let streetName = '';
        let city = '';
        let stateProvince = '';
        let postalCode = '';
        let countryCode = '';

        components.forEach((component) => {
          const types = component.types;
          const longText = component.longText ?? '';
          const shortText = component.shortText ?? '';
          if (types.includes('street_number')) {
            streetNumber = longText;
          }
          if (types.includes('route')) {
            streetName = longText;
          }
          if (
            types.includes('locality') ||
            types.includes('administrative_area_level_2')
          ) {
            city = longText;
          }
          if (types.includes('administrative_area_level_1')) {
            stateProvince = shortText;
          }
          if (types.includes('postal_code')) {
            postalCode = longText;
          }
          if (types.includes('country')) {
            countryCode = shortText;
          }
        });

        consecutiveTimeoutsRef.current = 0;

        return {
          streetAddress: `${streetNumber} ${streetName}`.trim(),
          city,
          stateProvince,
          postalCode,
          countryCode,
          placeId: place.id ?? placeId,
        };
      } catch (rawError) {
        const error =
          rawError instanceof Error ? rawError : new Error(String(rawError));
        if (error.message.includes('timed out')) {
          consecutiveTimeoutsRef.current += 1;
          if (
            consecutiveTimeoutsRef.current >=
            PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS
          ) {
            setAutocompleteFailed(true);
          }
        }
        reportFailure(error, 'getDetails');
        return null;
      } finally {
        setIsLoadingPlace(false);
      }
    },
    [apiKey, reportFailure],
  );

  return {
    placePredictions,
    isPredictionsLoading,
    isLoadingPlace,
    autocompleteFailed,
    getPlacePredictions,
    resolvePlaceDetails,
    clearPredictions,
  };
}
